import type {
  FolderChatSummary,
  FolderContentItem,
} from './folder-contents-page.model';

export const VIRTUAL_CHATS_ITEM_ID = '__virtual-chats__';

/**
 * Row id for one folder's collapsed "Chats" row. Namespaced by folder because
 * the tree view shows several at once — the open folder's own row plus one under
 * every expanded subfolder — and the list keys rows by id (`keyFn`), so a shared
 * constant id would collapse them into one row.
 */
export function virtualChatsItemId(folderId: string): string {
  return `${VIRTUAL_CHATS_ITEM_ID}:${folderId}`;
}

/**
 * Collapsed "Chats" row shown in a folder view. All of a folder's chats fold
 * into this single row (clicking it opens the folder chat history), so chats
 * are fetched separately from artifacts — see `fetchFolderContentsForFolder`.
 */
export type VirtualChatsListItem = {
  type: 'virtual-chats';
  data: {
    id: string;
    /**
     * The folder these chats belong to. Carried on the row rather than read from
     * the page, because an expanded subfolder's Chats row belongs to that
     * subfolder — clicking it must open *its* chat history, not the page's.
     */
    folderId: string;
    title: 'Chats';
    count: number;
    updatedAt: string;
    createdAt: string;
  };
};

export type FolderContentsListItem = FolderContentItem | VirtualChatsListItem;

export function isVirtualChatsItem(
  item: FolderContentsListItem
): item is VirtualChatsListItem {
  return item.type === 'virtual-chats';
}

/**
 * Builds the rows for a folder view: the collapsed "Chats" summary row (from
 * the server-provided, trigger-source-filtered chat summary) followed by the
 * individually-listed non-chat artifacts.
 *
 * Chats are fetched separately from artifacts (COT-6561), so `rawItems`
 * normally holds only non-chat items and a chat-heavy folder can never crowd
 * artifacts out of the page. The "Chats" row's count/timestamp come from
 * `chatSummary` so they match what the row opens into (the folder chat history,
 * which shows user-initiated chats only). `chatsInPage` is a defensive fallback
 * for any chats that do appear in `rawItems`. Returns `[]` at the top level,
 * where chats live in the stream rather than the Library root.
 *
 * Used for the open folder's own rows AND for each inline-expanded subfolder's
 * children, so an expanded folder discloses the same Chats row it would show
 * when opened as a page — hence `folderId`, which the row carries so its click
 * target is its own folder.
 */
export function buildFolderNestedListItems(params: {
  isTopLevel: boolean;
  /** Folder whose contents `rawItems` are. Unused at the top level. */
  folderId: string | undefined;
  rawItems: FolderContentItem[];
  chatSummary: FolderChatSummary | null | undefined;
}): FolderContentsListItem[] {
  const { isTopLevel, folderId, rawItems, chatSummary } = params;
  if (isTopLevel) {
    return [];
  }
  const nonChatItems = rawItems.filter((item) => item.type !== 'chat');
  const chatsInPage = rawItems.filter((item) => item.type === 'chat');

  const chatCount = chatSummary?.count ?? chatsInPage.length;
  // No folder id means no chat history to open, so the row would be a dead
  // click. Unreachable in practice: every non-top-level view has a folder id.
  if (chatCount === 0 || folderId === undefined) {
    return nonChatItems;
  }

  const newestChatUpdatedAtMs =
    chatSummary?.newestUpdatedAt !== undefined &&
    chatSummary?.newestUpdatedAt !== null
      ? new Date(chatSummary.newestUpdatedAt).getTime()
      : chatsInPage.reduce((latest, chat) => {
          const updatedAt = new Date(chat.data.updatedAt).getTime();
          return updatedAt > latest ? updatedAt : latest;
        }, 0);

  const virtualChatsItem: VirtualChatsListItem = {
    type: 'virtual-chats',
    data: {
      id: virtualChatsItemId(folderId),
      folderId,
      title: 'Chats',
      count: chatCount,
      updatedAt: new Date(newestChatUpdatedAtMs).toISOString(),
      createdAt: new Date(newestChatUpdatedAtMs).toISOString(),
    },
  };

  return [virtualChatsItem, ...nonChatItems];
}

/**
 * The same rows, for every inline-expanded folder in the tree.
 *
 * An expanded folder discloses what it would show when opened as a page, its
 * collapsed "Chats" row included — chats are excluded from the children fetch
 * (COT-6561), so they only reach the tree through here.
 *
 * Returns a new map rather than folding the rows into `childrenByFolderId`: that
 * one stays the raw fetched slice `hiddenChildCounts` measures against the server
 * total, and a virtual row counted there would understate the remainder by one.
 */
export function buildExpandedChildrenListItems(params: {
  childrenByFolderId: ReadonlyMap<string, FolderContentItem[]>;
  chatSummaryByFolderId: ReadonlyMap<string, FolderChatSummary>;
}): ReadonlyMap<string, FolderContentsListItem[]> {
  const { childrenByFolderId, chatSummaryByFolderId } = params;
  const rowsByFolderId = new Map<string, FolderContentsListItem[]>();
  for (const [folderId, children] of childrenByFolderId) {
    rowsByFolderId.set(
      folderId,
      buildFolderNestedListItems({
        isTopLevel: false,
        folderId,
        rawItems: children,
        chatSummary: chatSummaryByFolderId.get(folderId) ?? null,
      })
    );
  }
  return rowsByFolderId;
}
